import { useState, useEffect, useMemo } from 'react'

export type CardTemplate = 'compact' | 'image-focus' | 'details-rich' | 'list-view'
export type CardSize = 'small' | 'medium' | 'large'
export type FontSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl'

export interface CardFieldSettings {
  visible: boolean
  order: number
  fontSize?: FontSize
  bold?: boolean
  italic?: boolean
  underline?: boolean
}

// Advanced Configuration Types
export interface AdvancedFieldConfig {
  fieldKey: string
  prefix?: string  // Texto antes do valor (ex: "ID: ")
  suffix?: string  // Texto depois do valor (ex: " km")
  separator?: string  // Separador entre campos quando combinados (ex: " | ")
}

export interface AdvancedSectionConfig {
  type: 'single' | 'combined'  // Campo único ou combinação de campos
  fields: AdvancedFieldConfig[]
  fontSize?: FontSize
  bold?: boolean
  italic?: boolean
  underline?: boolean
  color?: string  // Cor customizada (hex)
}

export type BodyLayout = 'list' | 'grid-2' | 'inline'

export interface AdvancedBodyConfig {
  layout: BodyLayout
  fields: Array<{
    key: string
    label?: string  // Label customizado (se não definido, usa o original)
    visible: boolean
    fontSize?: FontSize
    bold?: boolean
    color?: string
    showLabel?: boolean  // Se false, mostra só o valor
  }>
}

export interface AdvancedCardConfig {
  enabled: boolean
  title?: AdvancedSectionConfig
  subtitle?: AdvancedSectionConfig
  badge?: {
    fieldKey: string
    variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
  }
  body?: AdvancedBodyConfig
}

export interface CardCustomizationSettings {
  template: CardTemplate
  size: CardSize
  fields: Record<string, CardFieldSettings>
  titleFontSize: FontSize
  subtitleFontSize: FontSize
  advanced?: AdvancedCardConfig  // Configuração avançada opcional
  grouping?: {
    mode: 'normal' | 'kanban'
    fieldKey?: string
    // Optional list of column values (string representation) in desired order
    columns?: string[]
  }
}

interface UseCardCustomizationOptions {
  storageKey: string
  defaultFields: string[]
  defaultTemplate?: CardTemplate
  defaultSize?: CardSize
  preferDefaultTemplate?: boolean
}

export function useCardCustomization({
  storageKey,
  defaultFields,
  defaultTemplate = 'compact',
  defaultSize = 'medium',
  preferDefaultTemplate = false
}: UseCardCustomizationOptions) {
  // Estado das configurações
  const [settings, setSettings] = useState<CardCustomizationSettings>(() => {
    // Tentar carregar do localStorage
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(storageKey)
        if (stored) {
          const parsed = JSON.parse(stored) as CardCustomizationSettings
          return preferDefaultTemplate
            ? { ...parsed, template: defaultTemplate }
            : parsed
        }
      } catch (error) {
        console.error('Erro ao carregar configurações do card:', error)
      }
    }

    // Configuração padrão
    return {
      template: defaultTemplate,
      size: defaultSize,
      titleFontSize: 'lg',
      subtitleFontSize: 'xs',
      fields: defaultFields.reduce((acc, field, index) => {
        acc[field] = { 
          visible: true, 
          order: index,
          fontSize: 'sm',
          bold: false,
          italic: false,
          underline: false
        }
        return acc
      }, {} as Record<string, CardFieldSettings>)
    }
  })

  // Persistir no localStorage quando mudar
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(storageKey, JSON.stringify(settings))
      } catch (error) {
        console.error('Erro ao salvar configurações do card:', error)
      }
    }
  }, [settings, storageKey])

  // Campos ordenados e visíveis
  const visibleFields = useMemo(() => {
    return Object.entries(settings.fields)
      .filter(([, config]) => config.visible)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([key]) => key)
  }, [settings.fields])

  // Funções de manipulação
  const toggleField = (fieldKey: string) => {
    setSettings(prev => ({
      ...prev,
      fields: {
        ...prev.fields,
        [fieldKey]: {
          ...prev.fields[fieldKey],
          visible: !prev.fields[fieldKey]?.visible
        }
      }
    }))
  }

  const setFieldStyle = (
    fieldKey: string, 
    style: Partial<Pick<CardFieldSettings, 'fontSize' | 'bold' | 'italic' | 'underline'>>
  ) => {
    setSettings(prev => ({
      ...prev,
      fields: {
        ...prev.fields,
        [fieldKey]: {
          ...prev.fields[fieldKey],
          ...style
        }
      }
    }))
  }

  const reorderFields = (fromIndex: number, toIndex: number) => {
    const fieldKeys = Object.keys(settings.fields).sort(
      (a, b) => settings.fields[a].order - settings.fields[b].order
    )

    const [movedKey] = fieldKeys.splice(fromIndex, 1)
    fieldKeys.splice(toIndex, 0, movedKey)

    setSettings(prev => ({
      ...prev,
      fields: fieldKeys.reduce((acc, key, index) => {
        acc[key] = {
          ...prev.fields[key],
          order: index
        }
        return acc
      }, {} as Record<string, CardFieldSettings>)
    }))
  }

  const setTemplate = (template: CardTemplate) => {
    setSettings(prev => ({ ...prev, template }))
  }

  const setSize = (size: CardSize) => {
    setSettings(prev => ({ ...prev, size }))
  }

  const setTitleFontSize = (fontSize: FontSize) => {
    setSettings(prev => ({ ...prev, titleFontSize: fontSize }))
  }

  const setSubtitleFontSize = (fontSize: FontSize) => {
    setSettings(prev => ({ ...prev, subtitleFontSize: fontSize }))
  }

  const resetToDefaults = () => {
    setSettings({
      template: defaultTemplate,
      size: defaultSize,
      titleFontSize: 'lg',
      subtitleFontSize: 'xs',
      fields: defaultFields.reduce((acc, field, index) => {
        acc[field] = { 
          visible: true, 
          order: index,
          fontSize: 'sm',
          bold: false,
          italic: false,
          underline: false
        }
        return acc
      }, {} as Record<string, CardFieldSettings>)
    })
  }

  // Classes CSS baseadas nas configurações
  const getSizeClasses = () => {
    switch (settings.size) {
      case 'small':
        return {
          padding: 'p-4',
          gap: 'gap-2',
          iconSize: 'w-5 h-5',
          iconPadding: 'p-1.5'
        }
      case 'large':
        return {
          padding: 'p-8',
          gap: 'gap-4',
          iconSize: 'w-8 h-8',
          iconPadding: 'p-3'
        }
      default: // medium
        return {
          padding: 'p-6',
          gap: 'gap-3',
          iconSize: 'w-6 h-6',
          iconPadding: 'p-2'
        }
    }
  }

  const getFontSizeClass = (fontSize: FontSize) => {
    const sizeMap: Record<FontSize, string> = {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl'
    }
    return sizeMap[fontSize]
  }

  const getFieldClasses = (fieldKey: string) => {
    const fieldSettings = settings.fields[fieldKey]
    if (!fieldSettings) return ''

    const classes = [
      getFontSizeClass(fieldSettings.fontSize || 'sm'),
      fieldSettings.bold ? 'font-bold' : 'font-medium',
      fieldSettings.italic ? 'italic' : '',
      fieldSettings.underline ? 'underline' : ''
    ]

    return classes.filter(Boolean).join(' ')
  }

  // Advanced Configuration Methods
  const enableAdvancedMode = () => {
    setSettings(prev => ({
      ...prev,
      advanced: {
        enabled: true,
        title: {
          type: 'single',
          fields: [{ fieldKey: defaultFields[0] || '' }],
          fontSize: 'lg',
          bold: true
        },
        subtitle: {
          type: 'single',
          fields: [{ fieldKey: defaultFields[1] || '' }],
          fontSize: 'sm'
        }
      }
    }))
  }

  const disableAdvancedMode = () => {
    setSettings(prev => ({
      ...prev,
      advanced: undefined
    }))
  }

  const setAdvancedTitle = (config: AdvancedSectionConfig) => {
    setSettings(prev => ({
      ...prev,
      advanced: {
        ...(prev.advanced || { enabled: true }),
        title: config
      }
    }))
  }

  const setAdvancedSubtitle = (config: AdvancedSectionConfig) => {
    setSettings(prev => ({
      ...prev,
      advanced: {
        ...(prev.advanced || { enabled: true }),
        subtitle: config
      }
    }))
  }

  const setAdvancedBadge = (config: { fieldKey: string; variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info' } | null) => {
    setSettings(prev => ({
      ...prev,
      advanced: {
        ...(prev.advanced || { enabled: true }),
        badge: config || undefined
      }
    }))
  }

  const setAdvancedBody = (config: AdvancedBodyConfig) => {
    setSettings(prev => ({
      ...prev,
      advanced: {
        ...(prev.advanced || { enabled: true }),
        body: config
      }
    }))
  }

  // Renderiza uma seção avançada combinando campos
  const renderAdvancedSection = (
    config: AdvancedSectionConfig,
    entityData: Record<string, any>
  ): string => {
    if (config.type === 'single' && config.fields.length > 0) {
      const field = config.fields[0]
      const value = entityData[field.fieldKey] || ''
      return `${field.prefix || ''}${value}${field.suffix || ''}`
    }
    
    // Tipo 'combined' - combina múltiplos campos
    return config.fields
      .map((field, index) => {
        const value = entityData[field.fieldKey] || ''
        const prefix = field.prefix || ''
        const suffix = field.suffix || ''
        const separator = index < config.fields.length - 1 ? (field.separator || '') : ''
        return `${prefix}${value}${suffix}${separator}`
      })
      .join('')
  }

  return {
    settings,
    visibleFields,
    toggleField,
    setFieldStyle,
    reorderFields,
    setTemplate,
    setSize,
    setTitleFontSize,
    setSubtitleFontSize,
    resetToDefaults,
    getSizeClasses,
    getFontSizeClass,
    getFieldClasses,
    // Grouping
    setGroupingMode: (mode: 'normal' | 'kanban') => setSettings(prev => ({ ...prev, grouping: { ...(prev.grouping || { mode }), mode } })),
    setGroupingField: (fieldKey?: string) => setSettings(prev => ({ ...prev, grouping: { ...(prev.grouping || { mode: 'normal' }), fieldKey } })),
    setGroupingColumns: (columns: string[]) => setSettings(prev => ({ ...prev, grouping: { ...(prev.grouping || { mode: 'normal', columns: [] }), columns } })),
    // Advanced config methods
    enableAdvancedMode,
    disableAdvancedMode,
    setAdvancedTitle,
    setAdvancedSubtitle,
    setAdvancedBadge,
    setAdvancedBody,
    renderAdvancedSection
  }
}

// Controller type exported for optional external control
export type CardCustomizationController = ReturnType<typeof useCardCustomization>
